import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Divider,
  useTheme,
  alpha,
  Collapse,
  IconButton,
  ListItemIcon,
  useMediaQuery
} from '@mui/material';
import { ChevronRight, ExpandMore, ChevronLeft } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import LockIcon from '@mui/icons-material/Lock';

// Helper function to format field names for display
const formatFieldName = (fieldName) => {
  // Replace underscores with spaces and capitalize first letter
  if (typeof fieldName !== 'string') return fieldName;

  // Replace underscores with spaces
  const withSpaces = fieldName.replace(/_/g, ' ');

  // Capitalize first letter of each word
  return withSpaces
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const renderLockedMenuItem = (text) => (
  <ListItemButton
    disabled
    sx={{
      pl: 3,
      py: 0.1,
      minHeight: 32,
      opacity: 1,
      cursor: 'not-allowed'
    }}
  >
    <ListItemIcon sx={{ minWidth: 36 }}>
      <LockIcon fontSize="small" color="disabled" />
    </ListItemIcon>
    <ListItemText
      primary={
        <Typography
          variant="body2"
          sx={{
            color: 'text.primary',
            fontSize: '0.775rem'
          }}
        >
          {formatFieldName(text)}
        </Typography>
      }
    />
  </ListItemButton>
);

const renderLockedCategoryHeader = (categoryName, expanded, onClick) => (
  <ListItemButton
    disabled={false}
    onClick={onClick}
    sx={{
      bgcolor: 'background.neutral',
      py: 0.5,
      minHeight: 36,
      opacity: 1,
      cursor: 'pointer'
    }}
  >
    <ListItemIcon sx={{ minWidth: 36 }}>
      <LockIcon fontSize="small" color="disabled" />
    </ListItemIcon>
    <ListItemText
      primary={
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
          {categoryName}
        </Typography>
      }
    />
    <IconButton
      size="small"
      edge="end"
      sx={{ p: 0.5 }}
      tabIndex={-1}
      disabled={false}
      onClick={(e) => {
        e.stopPropagation();
        onClick && onClick(e);
      }}
    >
      {expanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
    </IconButton>
  </ListItemButton>
);

const ReportSecondaryMenu = ({ categories, activeField, onFieldSelect, visible = false, onMinimizeToggle, access }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const [expandedSections, setExpandedSections] = React.useState({});
  const [minimized, setMinimized] = React.useState(isMobile);
  const initializedRef = React.useRef(false);

  // Get drawer state from Redux store
  const { drawerOpen } = useSelector((state) => state.menu);

  // Get the main menu width based on drawer state
  const getMainMenuWidth = () => {
    return drawerOpen ? 195 : 64; // Common drawer widths in Material UI
  };

  // Initialize from localStorage or set default expanded state only on first render
  React.useEffect(() => {
    if (initializedRef.current) return;

    try {
      // Try to load from localStorage
      const storedExpanded = localStorage.getItem('reportMenuExpandedSections');
      if (storedExpanded) {
        setExpandedSections(JSON.parse(storedExpanded));
      } else {
        // First time - initialize default with all sections expanded
        const initialExpanded = {};
        Object.keys(categories).forEach((key) => {
          initialExpanded[key] = true;
        });
        setExpandedSections(initialExpanded);
      }
    } catch (e) {
      // Fallback if localStorage fails
      const initialExpanded = {};
      Object.keys(categories).forEach((key) => {
        initialExpanded[key] = true;
      });
      setExpandedSections(initialExpanded);
    }

    initializedRef.current = true;
  }, [categories]);

  // Save to localStorage whenever expandedSections changes
  React.useEffect(() => {
    if (!initializedRef.current) return;

    try {
      localStorage.setItem('reportMenuExpandedSections', JSON.stringify(expandedSections));
    } catch (e) {
      // Silently fail if localStorage isn't available
      console.warn('Could not save menu state to localStorage');
    }
  }, [expandedSections]);

  const toggleSection = (section, event) => {
    // Stop event propagation to prevent unexpected behavior
    if (event) {
      event.stopPropagation();
    }

    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleMinimizeToggle = () => {
    const newMinimized = !minimized;
    setMinimized(newMinimized);
    if (onMinimizeToggle) {
      onMinimizeToggle(newMinimized);
    }
  };

  // If not visible, don't render
  if (!visible) return null;

  // If minimized, only show the expand button
  if (minimized) {
    return (
      <IconButton
        size="small"
        color="default"
        aria-label="expand menu"
        onClick={handleMinimizeToggle}
        sx={{
          position: 'fixed',
          top: isMobile ? 80 : 11,
          left: isMobile ? 0 : theme.direction === 'rtl' ? 'auto' : getMainMenuWidth(),
          right: isMobile ? 'auto' : theme.direction === 'rtl' ? getMainMenuWidth() : 'auto',
          zIndex: 2500,
          pointerEvents: 'auto',
          boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
          bgcolor: 'background.paper',
          color: 'text.primary',
          transition: 'left 0.3s ease',
          '&:hover': {
            bgcolor: 'background.paper',
            opacity: 0.9
          }
        }}
      >
        <ChevronRight fontSize="small" />
      </IconButton>
    );
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: isMobile ? 0 : theme.direction === 'rtl' ? 'auto' : getMainMenuWidth(),
        right: isMobile ? 0 : theme.direction === 'rtl' ? getMainMenuWidth() : 'auto',
        width: isMobile ? '100%' : 220,
        height: '100vh',
        backgroundColor: theme.palette.background.paper,
        borderRight: '1px solid',
        borderLeft: theme.direction === 'rtl' ? '1px solid' : 'none',
        borderColor: 'divider',
        zIndex: isMobile ? 1301 : 999,
        overflowY: 'auto',
        overflowX: 'hidden',
        transition: 'left 0.3s ease, right 0.3s ease, width 0.3s ease',
        boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
        '&::-webkit-scrollbar': {
          width: '4px'
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent'
        },
        '&::-webkit-scrollbar-thumb': {
          background: theme.palette.primary.light,
          borderRadius: '4px'
        }
      }}
    >
      <Box
        sx={{
          bgcolor: theme.palette.background.paper,
          color: 'text.primary',
          py: 1,
          px: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          zIndex: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 2px 4px rgba(0,0,0,0.03)'
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Competitive report
        </Typography>
        <IconButton size="small" onClick={handleMinimizeToggle} sx={{ color: 'text.secondary' }}>
          <ChevronLeft fontSize="small" />
        </IconButton>
      </Box>

      <List component="nav" dense disablePadding>
        {Object.entries(categories).map(([categoryName, category]) => {
          const isSideBySideCategory = categoryName === 'Side-by-Side Analysis';

          // If SBS is blocked, lock the parent and all children, but allow expand/collapse
          if (isSideBySideCategory && access?.features?.side_by_side_analysis?.enabled === false) {
            const expanded = !!expandedSections[categoryName];
            return (
              <React.Fragment key={categoryName}>
                {renderLockedCategoryHeader(categoryName, expanded, (e) => toggleSection(categoryName, e))}
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding dense>
                    {category.fields.map((field) => {
                      const fieldName = field.name || field;
                      return (
                        <ListItem key={fieldName} disablePadding>
                          {renderLockedMenuItem(fieldName)}
                        </ListItem>
                      );
                    })}
                  </List>
                </Collapse>
                <Divider />
              </React.Fragment>
            );
          }

          // Normal rendering for other categories
          return (
            <React.Fragment key={categoryName}>
              <ListItemButton
                onClick={(event) => {
                  if (categoryName === 'USP & Distinctions') {
                    onFieldSelect({
                      category: categoryName,
                      field: { name: 'USP & Distinctions' }
                    });
                    if (isMobile) setMinimized(true);
                  } else {
                    toggleSection(categoryName, event);
                  }
                }}
                sx={{
                  bgcolor: 'background.neutral',
                  py: 0.5,
                  minHeight: 36
                }}
              >
                <ListItemText
                  primary={
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {categoryName}
                    </Typography>
                  }
                />
                {categoryName !== 'USP & Distinctions' && (
                  <IconButton onClick={(event) => toggleSection(categoryName, event)} size="small" edge="end" sx={{ p: 0.5 }}>
                    {expandedSections[categoryName] ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
                  </IconButton>
                )}
              </ListItemButton>

              {categoryName !== 'USP & Distinctions' && (
                <Collapse in={!!expandedSections[categoryName]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding dense>
                    {category.fields.map((field) => {
                      const fieldName = field.name || field;
                      return (
                        <ListItem key={fieldName} disablePadding>
                          <ListItemButton
                            onClick={() => {
                              onFieldSelect({ field, category: categoryName });
                              if (isMobile) setMinimized(true);
                            }}
                            sx={{
                              pl: 3,
                              py: 0.1,
                              minHeight: 32,
                              borderLeft: '3px solid',
                              borderColor: activeField === fieldName ? 'primary.main' : 'transparent',
                              bgcolor: activeField === fieldName ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                              '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.04)
                              }
                            }}
                          >
                            <ListItemText
                              primary={
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: activeField === fieldName ? 600 : 400,
                                    color: activeField === fieldName ? 'primary.main' : 'text.primary',
                                    fontSize: '0.775rem'
                                  }}
                                >
                                  {formatFieldName(fieldName)}
                                </Typography>
                              }
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Collapse>
              )}
              <Divider />
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  );
};

ReportSecondaryMenu.propTypes = {
  categories: PropTypes.object.isRequired,
  activeField: PropTypes.string,
  onFieldSelect: PropTypes.func.isRequired,
  visible: PropTypes.bool,
  onMinimizeToggle: PropTypes.func,
  access: PropTypes.object
};

export default ReportSecondaryMenu;
